import SideNavigationMenu from "@/shared/components/SideNavigationMenu/SideNavigationMenu";
import {
	setNavigationBlocked,
	showNavigationBlockedMessage,
} from "@/store/navigationBlock/navigationBlockActions";
import { useScreenSize } from "@/utils/media-query";
import { useMenuPatch } from "@/utils/patches";
import Button from "devextreme-react/button";
import { Template } from "devextreme-react/core/template";
import Drawer from "devextreme-react/drawer";
import { ScrollView } from "devextreme-react/scroll-view"; // Importa el tipo correcto
import Toolbar, { Item } from "devextreme-react/toolbar";
import { ClickEvent } from "devextreme/ui/button_types";
import dxScrollView, { InitializedEvent } from "devextreme/ui/scroll_view";
import { ItemClickEvent } from "devextreme/ui/tree_view_types";
import React, { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";
import "./SideNavInnerToolbar.scss";

interface SideNavToolbarProps {
	title: string;
	children: any;
	isInIframe?: boolean;
}

const isMenuClosed = (menuStatus: number): boolean => {
	return menuStatus === MenuStatus.Closed;
};

const isMenuTemporaryOpened = (menuStatus: number): boolean => {
	return menuStatus === MenuStatus.TemporaryOpened;
};

const toggleMenuCallback = (prevMenuStatus: number): number =>
	isMenuClosed(prevMenuStatus) ? MenuStatus.Opened : MenuStatus.Closed;

const temporaryOpenMenuCallback = (prevMenuStatus: number): number =>
	isMenuClosed(prevMenuStatus) ? MenuStatus.TemporaryOpened : prevMenuStatus;

const dashboardPath = "/dashboard";

const onOutsideClickCallback = (
	prevMenuStatus: number,
	isLarge: boolean,
): number =>
	!isMenuClosed(prevMenuStatus) && !isLarge
		? MenuStatus.Closed
		: prevMenuStatus;

export default function SideNavInnerToolbar({
	title,
	children,
	isInIframe = false,
}: Readonly<SideNavToolbarProps>) {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { isXSmallVertical, isXSmallHorizontal, isLarge } = useScreenSize();
	const [patchCssClass, onMenuReady] = useMenuPatch();
	const [scrollViewComponent, setScrollViewComponent] =
		useState<dxScrollView>();
	const [menuStatus, setMenuStatus] = useState(
		isLarge ? MenuStatus.Opened : MenuStatus.Closed,
	);

	const toggleMenu = useCallback(
		({ event }: ClickEvent) => {
			if (event) {
				event.stopPropagation();
				event.preventDefault();
			}

			if (isXSmallHorizontal || isXSmallVertical) {
				setMenuStatus((prevStatus) =>
					prevStatus === MenuStatus.Closed
						? MenuStatus.Opened
						: MenuStatus.Closed,
				);
			} else {
				setMenuStatus(toggleMenuCallback);
			}
		},
		[isXSmallHorizontal, isXSmallVertical],
	);

	const temporaryOpenMenu = useCallback(() => {
		setMenuStatus(temporaryOpenMenuCallback);
	}, []);

	const onOutsideClick = useCallback(() => {
		setMenuStatus((prevMenuStatus) =>
			onOutsideClickCallback(prevMenuStatus, isLarge),
		);

		return false;
	}, [isLarge]);

	const onNavigationChanged = useCallback(
		async ({ itemData, event }: ItemClickEvent) => {
			if (!itemData?.link) {
				event?.preventDefault();
				return;
			}

			// Solo verificar bloqueo de navegación si estamos en la ruta second-step
			const currentPath = window.location.pathname;
			const isInSecondStep = currentPath.includes("/second-step/");

			if (isInSecondStep) {
				try {
					const canNavigate = await dispatch(showNavigationBlockedMessage());
					if (!canNavigate) {
						event?.preventDefault();
						return;
					}
					// Limpiar el bloqueo inmediatamente después de aceptar
					dispatch(setNavigationBlocked(false));
				} catch (error) {
					event?.preventDefault();
					return;
				}
			}

			navigate(itemData.link);
			scrollViewComponent?.instance().scrollTo(0);

			if (!isLarge || isMenuTemporaryOpened(menuStatus)) {
				setMenuStatus(MenuStatus.Closed);
				event?.stopPropagation();
			}
		},
		[navigate, menuStatus, isLarge, dispatch],
	);

	function onInitialized(e: InitializedEvent) {
		setScrollViewComponent(e.component);
	}

	const handleNavigateToDashboard = useCallback(async () => {
		// Solo verificar bloqueo de navegación si estamos en la ruta second-step
		const currentPath = window.location.pathname;
		const isInSecondStep = currentPath.includes("/second-step/");

		if (isInSecondStep) {
			const canNavigate = await dispatch(showNavigationBlockedMessage());
			if (!canNavigate) {
				return;
			}
			// Limpiar el bloqueo inmediatamente después de aceptar
			dispatch(setNavigationBlocked(false));
		}

		navigate(dashboardPath);
	}, [dispatch, navigate]);

	return (
		<div className={"side-nav-inner-toolbar"}>
			{!isInIframe && (
				<Drawer
					className={[
						"drawer",
						patchCssClass,
						menuStatus === MenuStatus.Closed
							? "drawer-collapsed"
							: "drawer-expanded",
					].join(" ")}
					position={"before"}
					closeOnOutsideClick={onOutsideClick}
					openedStateMode={!isLarge ? "overlap" : "shrink"}
					revealMode={
						isXSmallHorizontal || isXSmallVertical ? "slide" : "expand"
					}
					minSize={isXSmallHorizontal || isXSmallVertical ? 0 : 74}
					maxSize={281}
					shading={!isLarge}
					opened={menuStatus !== MenuStatus.Closed}
					template={"menu"}
				>
					<Template name={"menu"}>
						<SideNavigationMenu
							compactMode={
								isXSmallHorizontal || isXSmallVertical
									? false
									: menuStatus === MenuStatus.Closed
							}
							selectedItemChanged={onNavigationChanged}
							openMenu={temporaryOpenMenu}
							onMenuReady={onMenuReady}
						>
							<Toolbar id={"navigation-header"}>
								{!isMenuClosed(menuStatus) && (
									<Item
										location={isMenuClosed(menuStatus) ? "after" : "center"}
										cssClass={"header-title"}
										text={title}
									>
										<div
											className="pointer"
											onClick={handleNavigateToDashboard}
											onKeyDown={(e) => {
												if (e.key === "Enter" || e.key === " ") {
													handleNavigateToDashboard();
												}
											}}
										>
											<div
												className={"logo-wisephere-menu"}
												onClick={handleNavigateToDashboard}
												onKeyDown={(e) => {
													if (e.key === "Enter" || e.key === " ") {
														handleNavigateToDashboard();
													}
												}}
												role="button"
												tabIndex={0}
											/>
										</div>
									</Item>
								)}
								<Item
									location={isMenuClosed(menuStatus) ? "before" : "after"}
									cssClass={
										!isMenuClosed(menuStatus)
											? "menu-button"
											: "menu-button-hide"
									}
								>
									<Button
										icon={isMenuClosed(menuStatus) ? "menu-close" : "menu-open"}
										elementAttr={{
											class: isMenuClosed(menuStatus) ? "ms-3" : "",
										}}
										stylingMode="text"
										onClick={toggleMenu}
									/>
								</Item>
							</Toolbar>
						</SideNavigationMenu>
					</Template>

					{/* Mueve aquí el scroll-container */}
					<div className={"scroll-container"}>
						<Header
							menuToggleEnabled={isXSmallHorizontal || isXSmallVertical}
							toggleMenu={toggleMenu}
							title={""}
						/>
						<ScrollView
							className={"layout-body with-footer"}
							onInitialized={onInitialized}
						>
							<div className={"content"}>
								{React.Children.map(children, (item: any) => {
									return item.type !== Footer && item;
								})}
							</div>
							<div className={"content-block"}>
								{React.Children.map(children, (item: any) => {
									return item.type === Footer && item;
								})}
							</div>
						</ScrollView>
						<Footer />
					</div>
				</Drawer>
			)}
			{isInIframe && (
				<ScrollView
					onInitialized={onInitialized}
					className={"layout-body with-footer"}
				>
					<div className={"content"}>
						{React.Children.map(children, (item) => {
							return item.type !== Footer && item;
						})}
					</div>
					<div className={"content-block"}>
						{React.Children.map(children, (item) => {
							return item.type === Footer && item;
						})}
					</div>
				</ScrollView>
			)}
		</div>
	);
}

const MenuStatus = {
	Closed: 1,
	Opened: 2,
	TemporaryOpened: 3,
};
